
import Vue from 'vue'
import store from '../store';
import { INode } from "@/shared/models/node.model";
import { IContainer } from "@/shared/models/container.model";
import CartMenu from './CartMenu.vue';
import { HtmlHelper } from "@/shared/lib/htmlhelper";
import { ResponsibilityType } from '@/shared/enums/responsibilitytype.enum';
import { PdfExportService } from '@/services/pdf/pdf-export.service';
import { HtmlExportService } from '@/services/export/html-export.service';

export default Vue.extend({
    name: 'GuidelineToolbar',

    components: {
        CartMenu
    },
    props: {
        item: Object as () => INode,
        chapter: Object as () => INode,
        conditions: Object as () => IContainer
    },
    data() {
        return {
            snackbar: false,
            isChapter: false as boolean,
            cartDialog: false as boolean,
            filterF: false as boolean,
            filterV: false as boolean,
            filterVb: false as boolean,
            pdfGenerating: false as boolean
        }
    },
    async mounted() {
        this.filterF = store.state.filter.includes(ResponsibilityType.F);
        this.filterV = store.state.filter.includes(ResponsibilityType.V);
        this.filterVb = store.state.filter.includes(ResponsibilityType.Vb);
    },
    watch: {
        async item() {
            this.isChapter = this.item.Type === 0;
        },
        async filterF(value: boolean) {
            let filter: ResponsibilityType[] = [
                ...(this.filterV ? [ResponsibilityType.V] : []),
                ...(this.filterVb ? [ResponsibilityType.Vb] : []),
                ...(value ? [ResponsibilityType.F] : [])
            ];

            store.commit("SET_FILTER", filter);
        },
        async filterV(value: boolean) {
            let filter: ResponsibilityType[] = [
                ...(value ? [ResponsibilityType.V] : []),
                ...(this.filterVb ? [ResponsibilityType.Vb] : []),
                ...(this.filterF ? [ResponsibilityType.F] : [])
            ];

            store.commit("SET_FILTER", filter);
        },
        async filterVb(value: boolean) {
            let filter: ResponsibilityType[] = [
                ...(this.filterV ? [ResponsibilityType.V] : []),
                ...(value ? [ResponsibilityType.Vb] : []),
                ...(this.filterF ? [ResponsibilityType.F] : [])
            ];

            store.commit("SET_FILTER", filter);
        }
    },
    methods: {
        async copyUrl() {
            const helper = new HtmlHelper();
            const url = helper.getBaseUrl() + this.item.Url;
            let successfull = await navigator.clipboard.writeText(url).then(function () {
                return true;
            }, function () {
                return false;
            });
            if (successfull) {
                this.snackbar = true;
            }
        },
        async printDocument() {
            let wasAutoPopulated = false;
            
            // If cart is empty, populate it with all items from current page
            if (!this.$store.state.cart || this.$store.state.cart.length === 0) {
                this.addAllCurrentPageItems();
                wasAutoPopulated = true;
                // Wait for Vue to process the state update
                await this.$nextTick();
            }
            
            // Generate HTML and open print dialog (if cart has items)
            if (this.$store.state.cart && this.$store.state.cart.length > 0) {
                HtmlExportService.printHtml();
                
                // If items were auto-added, clear the cart after a delay
                if (wasAutoPopulated) {
                    setTimeout(() => {
                        this.$store.commit("SET_CART", []);
                    }, 2000);
                }
            } else {
                // Fallback to regular print if no items found
                window.print();
            }
        },
        addAllCurrentPageItems(): void {
            if (!this.conditions) return;
            
            const items: any[] = [];
            this.collectIoContainers(this.conditions, items);
            
            const cart = [...this.$store.state.cart];
            
            // Add all collected items to cart
            items.forEach(ioContainer => {
                cart.push({
                    Id: ioContainer.Id,
                    Path: ioContainer.Path,
                    Url: this.item.Url,
                    Data: this.cloneItemData(ioContainer.item)
                });
            });
            
            this.$store.commit("SET_CART", cart);
        },
        collectIoContainers(node: IContainer, items: any[], parentPath = ''): void {
            if (!node) return;
            
            const currentPath = parentPath ? `${parentPath} / ${node.Name}` : node.Name;
            
            if (node.IsInventoryObject) {
                // This is an io-container, add it to collection
                items.push({
                    Id: node.Id,
                    Path: parentPath, // Use parent path, not including io-container name
                    item: node
                });
            } else if (node.Children && node.Children.length > 0) {
                // Recursively process children
                node.Children.forEach(child => {
                    this.collectIoContainers(child, items, currentPath);
                });
            }
        },
        cloneItemData(item: IContainer): any {
            // Deep clone the item data to store in cart
            return {
                Id: item.Id,
                Name: item.Name,
                Description: item.Description,
                Information: item.Information,
                Text: item.Text,
                Level: item.Level,
                IsInventoryObject: item.IsInventoryObject,
                GuidelineItems: item.GuidelineItems ? item.GuidelineItems.map((gi: any) => ({
                    Id: gi.Id,
                    Name: gi.Name,
                    Text: gi.Text,
                    Description: gi.Description,
                    Responsibility: gi.Responsibility,
                    Extended: gi.Extended ? {
                        Condition: gi.Extended.Condition,
                        Reference: gi.Extended.Reference,
                        ReferenceUrl: gi.Extended.ReferenceUrl
                    } : null,
                    Normal: gi.Normal ? {
                        Condition: gi.Normal.Condition,
                        Reference: gi.Normal.Reference,
                        ReferenceUrl: gi.Normal.ReferenceUrl
                    } : null
                })) : [],
                Children: item.Children ? item.Children.map((child: any) => this.cloneItemData(child)) : []
            };
        },
        async exportPdf(): Promise<void> {
            try {
                this.pdfGenerating = true;
                await PdfExportService.generatePdf();
            } catch (error) {
                alert('Ett fel uppstod vid generering av PDF');
            } finally {
                this.pdfGenerating = false;
            }
        },
        isAdmin(): boolean {
            return store.state.isTdAdmin;
        }
    },
})
